import {
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  PackageLifecycle,
  ServicePackageService,
  ServicePackagePurchase,
  ServicePackagePurchaseStatus,
  ServiceService,
  PackageLifecyclePipe,
  PackageLifecycleColorPipe,
} from '@beautyapp/core';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import {
  PurchaseEditModalComponent,
  openPackageRefundModal,
  openPackageCancelModal,
  isEarlierDay,
  openPackagePurchaseDrawer,
} from '@beautyapp/service-packages';

/**
 * beaut-497. Which slice of this customer's packages the table shows.
 * RECEIVED and GIVEN are the two directions of a gift, which the list has
 * always carried together with no way to ask for one.
 */
export type GiftFilter = 'ALL' | 'OWN' | 'RECEIVED' | 'GIVEN';

@Component({
  selector: 'beautyapp-user-packages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzRadioModule,
    NzSelectModule,
    NzTableModule,
    NzTagModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    NzProgressModule,
    TranslateModule,
    PackageLifecyclePipe,
    PackageLifecycleColorPipe,
  ],
  templateUrl: './user-packages.component.html',
  styles: [
    `
      /* beaut-497. The row badges were nz-tags sitting inline after the name.
         Two problems, both visible only in the running table: they widened
         this column until the numeric ones were pushed to the edge, and the
         preset tag colours rendered dark-on-dark — "აჩუქა — …" came out as
         grey text on a grey pill.

         So: their own line, their own scale, and explicit colours with a
         contrast ratio I can point at rather than a preset name I hoped for. */
      .pkg__filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }
      /* The two dropdowns answer a different question from the radio group,
         so they sit apart from it rather than in the same run of controls —
         pressed together they read as one long row of unrelated choices. */
      .pkg__selects {
        display: flex;
        gap: 8px;
        margin-left: auto;
      }
      .pkg__select {
        min-width: 160px;
      }
      .pkg__name {
        line-height: 1.3;
      }
      .pkg__badges {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 3px;
      }
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 0 6px;
        border-radius: 4px;
        border: 1px solid transparent;
        font-size: 11px;
        line-height: 18px;
        white-space: nowrap;
      }
      /* Received: the customer HOLDS this one. Warm, and the strongest of the
         three, because it is the one that changes what the operator can do. */
      .badge--gift {
        background: #fdf2f8;
        border-color: #fbcfe8;
        color: #9d174d;
      }
      /* Given away: informational, so it recedes — but still 7.5:1 on its own
         background rather than the grey-on-grey it replaced. */
      .badge--given {
        background: #f1f5f9;
        border-color: #cbd5e1;
        color: #334155;
      }
      .badge--edited {
        background: #fffbeb;
        border-color: #fde68a;
        color: #92400e;
      }
    `,
  ],
})
export class UserPackagesComponent {
  private packageService = inject(ServicePackageService);
  private serviceService = inject(ServiceService);
  private modalService = inject(NzModalService);
  private drawerService = inject(NzDrawerService);
  private translate = inject(TranslateService);
  private message = inject(NzMessageService);

  userId = input.required<string>();

  /**
   * beaut-497. Which slice of the list to show. 'ALL' unless the operator asks
   * for one.
   *
   * The gift indicator used to switch this when it opened the tab. It does not
   * any more: this is the SERVICE PACKAGES tab, and landing in a narrowed view
   * hides the rest of what the operator came to see.
   *
   * Three values because a gift has two directions and they are different
   * questions — "what was this customer given" and "what did they give away".
   * The list has always held both mixed together.
   */
  giftFilter = signal<GiftFilter>('ALL');

  /** Lifecycle state, or null for every state. Server-side; see the interface. */
  lifecycleFilter = signal<PackageLifecycle | null>(null);

  /** Sale status, or null for every status. */
  statusFilter = signal<ServicePackagePurchaseStatus | null>(null);

  /**
   * The two dropdowns' options. Built once here rather than in the template so
   * the i18n keys sit beside the values they label and cannot drift apart.
   */
  readonly lifecycleOptions: { value: PackageLifecycle; label: string }[] = [
    { value: 'NEW', label: 'servicePackages.lifecycle.new' },
    { value: 'PARTIALLY_USED', label: 'servicePackages.lifecycle.partiallyUsed' },
    { value: 'FULLY_USED', label: 'servicePackages.lifecycle.fullyUsed' },
    { value: 'EXPIRED', label: 'servicePackages.lifecycle.expired' },
    { value: 'CANCELLED', label: 'servicePackages.lifecycle.cancelled' },
  ];

  readonly statusOptions: { value: ServicePackagePurchaseStatus; label: string }[] = [
    { value: 'ACTIVE', label: 'servicePackages.status.ACTIVE' },
    { value: 'USED', label: 'servicePackages.status.USED' },
    { value: 'EXPIRED', label: 'servicePackages.status.EXPIRED' },
    { value: 'CANCELLED', label: 'servicePackages.status.CANCELLED' },
  ];

  page = signal(1);
  pageSize = signal(10);

  // In-flight guard for the edit modal. Opening it fires an async forkJoin before
  // modalService.create runs, so without this each rapid click stacked another
  // modal (#2908 §4). Re-entry is blocked while true; the trigger button also
  // binds to it so it visually disables.
  editOpening = signal(false);

  purchasesResource = rxResource({
    params: () => ({
      userId: this.userId(),
      page: this.page(),
      pageSize: this.pageSize(),
      giftFilter: this.giftFilter(),
      lifecycle: this.lifecycleFilter(),
      status: this.statusFilter(),
    }),
    stream: ({ params }) => {
      if (!params.userId) {
        return of({
          data: [],
          meta: { itemCount: 0, page: 1, pageSize: 10, pageCount: 0 },
        });
      }
      return this.packageService
        .getPurchases({
          userId: params.userId,
          page: params.page,
          pageSize: params.pageSize,
          // Omitted rather than sent as false: the unfiltered list is the
          // default on the wire, and a literal false would still read as "the
          // caller has an opinion about gifts".
          ...(params.giftFilter === 'OWN' ? { ownPurchase: true } : {}),
          ...(params.giftFilter === 'RECEIVED' ? { receivedAsGift: true } : {}),
          ...(params.giftFilter === 'GIVEN' ? { givenAsGift: true } : {}),
          ...(params.lifecycle ? { lifecycle: params.lifecycle } : {}),
          ...(params.status ? { status: params.status } : {}),
        })
        .pipe(
          catchError(() =>
            of({
              data: [],
              meta: { itemCount: 0, page: 1, pageSize: 10, pageCount: 0 },
            })
          )
        );
    },
  });

  purchases = computed(() => this.purchasesResource.value()?.data || []);
  total = computed(() => this.purchasesResource.value()?.meta?.itemCount || 0);
  loading = computed(() => this.purchasesResource.isLoading());

  /**
   * beaut-497. Switching narrows or widens the list, so page 3 of the old one
   * is very likely page nothing of the new one — an empty table that reads as
   * "this customer has none of these".
   */
  setGiftFilter(value: GiftFilter): void {
    this.giftFilter.set(value);
    this.page.set(1);
  }

  setLifecycleFilter(value: PackageLifecycle | null): void {
    this.lifecycleFilter.set(value);
    this.page.set(1);
  }

  setStatusFilter(value: ServicePackagePurchaseStatus | null): void {
    this.statusFilter.set(value);
    this.page.set(1);
  }

  /**
   * Which side of a gift this row is, seen from the customer whose page this
   * is. This list has always contained both directions (beaut-251 made the
   * userId filter match payer OR beneficiary) and never said which was which,
   * so a package Nino bought for Mari sat in Nino's list looking like Nino's
   * own. The data to tell them apart was already on the row; nothing rendered
   * it.
   *
   * 'received' — somebody else paid, this customer consumes it.
   * 'given'    — this customer paid, somebody else consumes it.
   * null       — an ordinary purchase, nothing to say.
   */
  giftDirection(purchase: ServicePackagePurchase): 'received' | 'given' | null {
    const payerId = purchase.payer?.id;
    if (!purchase.isGift || !payerId) {
      return null;
    }
    // `isGift` is `!!payerUserId` on the backend, which is also true when a
    // self-purchase happens to record the buyer as payer. Same person on both
    // sides is not a gift and must not be labelled as one.
    if (payerId === purchase.customer?.id) {
      return null;
    }
    return payerId === this.userId() ? 'given' : 'received';
  }

  getStatusColor(status: ServicePackagePurchaseStatus): string {
    const colors: Record<string, string> = {
      ACTIVE: 'green',
      USED: 'blue',
      CANCELLED: 'red',
      EXPIRED: 'default',
    };
    return colors[status] || 'default';
  }

  getUsagePercent(purchase: ServicePackagePurchase): number {
    if (purchase.totalServicesCount === 0) return 0;
    return Math.round(
      (purchase.usedServicesCount / purchase.totalServicesCount) * 100
    );
  }

  onPageChange(page: number): void {
    this.page.set(page);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize.set(pageSize);
  }

  // Aligned with the service-packages module list: open the shared drawer
  // (info header + edit history + operations) instead of the older detail modal.
  viewDetails(purchase: ServicePackagePurchase): void {
    openPackagePurchaseDrawer(this.drawerService, purchase.id);
  }

  cancelPurchase(purchase: ServicePackagePurchase): void {
    // Aligned with the module list (beaut-290): a USED package can't be plain-
    // cancelled (the BE rejects it) — route it through the refund engine where
    // the operator picks the return type + method. Fully-unused keeps the
    // lightweight confirm → revert path.
    if ((purchase.usedServicesCount ?? 0) > 0) {
      openPackageRefundModal(this.modalService, purchase).afterClose.subscribe(
        (refunded) => {
          if (refunded) this.purchasesResource.reload();
        }
      );
      return;
    }

    // beaut-484: an unused package sold on an earlier day asks which till day
    // its reversal lands on, as the refund modal does for a used one. Sold
    // today, both answers are the same till, so the plain confirm stays.
    if (isEarlierDay(purchase.purchasedAt ?? purchase.createdAt)) {
      openPackageCancelModal(this.modalService, this.translate, purchase).afterClose.subscribe(
        (cancelled) => {
          if (cancelled) this.purchasesResource.reload();
        }
      );
      return;
    }

    this.modalService.confirm({
      nzTitle: this.translate.instant('servicePackages.confirm.cancel'),
      nzContent: this.translate.instant(
        'servicePackages.confirm.cancelDescription'
      ),
      nzOkDanger: true,
      nzOnOk: () => {
        this.packageService.cancelPurchase(purchase.id, {}).subscribe({
          next: () => {
            this.message.success(
              this.translate.instant('servicePackages.messages.cancelled')
            );
            this.purchasesResource.reload();
          },
          error: (err) => {
            this.message.error(
              err.error?.message ||
                this.translate.instant('servicePackages.messages.cancelError')
            );
          },
        });
      },
    });
  }

  openEditModal(purchase: ServicePackagePurchase): void {
    if (this.editOpening()) {
      return;
    }
    this.editOpening.set(true);
    forkJoin({
      detail: this.packageService.getPurchaseById(purchase.id),
      catalog: this.serviceService.dropdown(),
    })
      .pipe(finalize(() => this.editOpening.set(false)))
      .subscribe({
        next: ({ detail, catalog }) => {
          const purchaseItems = (detail.services ?? []).map((s: any) => ({
            id: s.packageItemId,
            serviceId: s.serviceId,
            serviceName: s.serviceName,
            // Clamp to the domain floor (≥1). A legacy row with a null/0
            // quantity would otherwise hydrate the required+min(1) control as
            // an empty, invalid field that the operator can't easily fix.
            quantity: Number(s.totalQuantity) || 1,
            originalPrice: s.originalPrice,
            discountedPrice: s.discountedPrice,
          }));
          const rawCatalog: any[] = catalog?.data ?? catalog ?? [];
          const servicesCatalog = rawCatalog.map((s: any) => ({
            id: s.id,
            name: s.name,
            price: Number(s.price ?? 0),
            discountPrice: Number(s.discountPrice ?? s.price ?? 0),
          }));
          this.modalService
            .create({
              nzTitle: 'პაკეტის რედაქტირება',
              nzContent: PurchaseEditModalComponent,
              nzWidth: 800,
              nzData: {
                purchaseId: detail.id,
                initialPurchase: {
                  ...detail,
                  displayName:
                    (detail as any).displayName ?? detail?.package?.name ?? '',
                  comment: detail.comment ?? null,
                  purchaseItems,
                  usages: detail.usages ?? [],
                },
                servicesCatalog,
              },
              nzFooter: null,
            })
            .afterClose.subscribe((result) => {
              if (result?.success) {
                this.purchasesResource.reload();
              }
            });
        },
        error: () => {
          this.message.error(
            this.translate.instant('servicePackages.messages.loadError')
          );
        },
      });
  }
}
